import { useState, useRef, useCallback, useEffect } from 'react'
import * as d3 from 'd3'

import {
  formatPoint,
  degreesToRadians,
  radiansToDegrees,
  calcCirclePosition,
  calcCircleAngle,
  calcDistance,
  calcMiddlePoint,
  calcCentroid,
  calcArcs,
  calcCoef
} from '@aytm/app/stats/page/data-centrifuge/lib/utils'
import { getVectorColor, getVisibleRaw } from '@aytm/app/stats/page/data-centrifuge/lib/vectors'

// Default configuration values from model.coffee
export const DEFAULT_CONFIG = {
  vectorRadius: 40,
  vectorGravity: 10,
  minScoreRadius: 3,
  maxScoreRadius: 20,
  curveAngle: 50,
  maxD: 11,
  minD: 1,
  power: 7,
  padding: 8,
  linkOpacity: {
    single: {
      min: 0.1,
      power: 0.21
    },
    multi: {
      coef: 0.5,
      min: 0,
      power: 0.25
    }
  },
  stroke: 2,
  jitter: true,
  dataCacheEnabled: true
}

/**
 * Custom hook for data calculation logic
 * Converted from model.coffee Backbone.Model
 */
export const useDataCalculation = ({
  width,
  job,
  vectors,
  disabledVectors,
  filter,
  suspicionLevel,
  scores,
  suspicionScores,
  quarantineUIDs,
  dataVersion,
  vectorRadius = DEFAULT_CONFIG.vectorRadius,
  vectorGravity = DEFAULT_CONFIG.vectorGravity,
  minScoreRadius = DEFAULT_CONFIG.minScoreRadius,
  maxScoreRadius = DEFAULT_CONFIG.maxScoreRadius,
  curveAngle = DEFAULT_CONFIG.curveAngle,
  maxD = DEFAULT_CONFIG.maxD,
  minD = DEFAULT_CONFIG.minD,
  power = DEFAULT_CONFIG.power,
  padding = DEFAULT_CONFIG.padding,
  jitter = DEFAULT_CONFIG.jitter,
  dataCacheEnabled = DEFAULT_CONFIG.dataCacheEnabled
}) => {
  const [data, setData] = useState(null)

  const dataCacheRef = useRef({})
  const dataCacheVersionRef = useRef(dataVersion)
  const dataStartRef = useRef(null)
  const calcTimerRef = useRef(null)

  const chartWidth = width - 2
  const chartHeight = width - 2

  // Initialize scores with positions
  const initializeScores = useCallback(
    dataObj => {
      if (!dataObj.indexes.length) return dataObj

      const Q = vectorRadius + maxScoreRadius
      const K = calcCoef(dataObj.indexes.length, vectors.length, maxD, minD)

      // Per-score eligibility precomputed once. Replaces the per-i `.includes` scan
      // against quarantineUIDs and the duplicated filter-mode booleans.
      const quarantineLookup = {}
      quarantineUIDs.forEach(uid => {
        quarantineLookup[uid] = true
      })

      const scoreFilterPass = scores.map(s => {
        const isQuarantined = quarantineLookup[s[0]]
        let pass = suspicionLevel <= suspicionScores[s[0]]
        pass =
          pass &&
          (filter == 'all' || (!isQuarantined && filter != 'quarantined') || (isQuarantined && filter != 'flagged'))
        return pass
      })

      const scoresIndexes = scores
        .map((_, i) => i)
        .filter(i => scoreFilterPass[i] && dataObj.indexes.some(j => 0 < scores[i][j + 1]))

      const scoresAllIndexes = scoresIndexes.filter(
        i => dataObj.indexes.reduce((acc, cur) => acc + (scores[i][cur + 1] > 0 ? 1 : 0), 0) > 1
      )

      // vs entries carry a precomputed `clamped` (= Math.min(1, Math.max(0, s[v.i]))) so
      // calcRadius / calcPosition / the _weight reduce don't redo the same clamp 4–5×.
      const calcRadius = (s, vs) => {
        let r = minScoreRadius
        if (dataObj.indexes.length > 1) {
          if (vs.length > 1) {
            const x = vs.reduce((acc, cur) => acc + cur.clamped, 0) / vs.length
            const k =
              1 -
              (K * (1 - Math.pow(x, power)) + (1 - K) * ((dataObj.indexes.length - vs.length) / dataObj.indexes.length))
            r =
              maxScoreRadius -
              (1 - Math.pow(k, (3 * scoresAllIndexes.length) / 900 + 15 / 9)) * (maxScoreRadius - minScoreRadius)
          } else {
            r = minScoreRadius - (1 - vs[0].clamped) * (minScoreRadius / 4)
          }
        }
        return r
      }

      const calcPosition = (s, vs, r, w) => {
        if (vs.length > 2) {
          const _R = dataObj.R - Q

          const coords = vs.map((v, i) => {
            const p = calcCirclePosition(v.x, v.y, _R)
            const _v = vs[(i + 1) % vs.length]
            const _p = calcCirclePosition(_v.x, _v.y, _R)

            const k = _v.clamped / (v.clamped + _v.clamped)
            return { x: p.x + k * (_p.x - p.x), y: p.y + k * (_p.y - p.y) }
          })
          return calcCentroid(coords)
        } else if (vs.length > 1) {
          const a = calcCircleAngle(vs[0], vs[1], dataObj.diameter / 2)

          if (a > 90) {
            const d = calcDistance(vs[0], vs[1])
            const _d = Math.max(0, d - 2 * Q)

            const _k1 = Q / d
            const _k2 = (_d + Q) / d
            const _x1 = vs[0].x + _k1 * (vs[1].x - vs[0].x)
            const _y1 = vs[0].y + _k1 * (vs[1].y - vs[0].y)
            const _x2 = vs[0].x + _k2 * (vs[1].x - vs[0].x)
            const _y2 = vs[0].y + _k2 * (vs[1].y - vs[0].y)

            const _s = _d > 0 ? (maxScoreRadius * (vs[0].clamped + vs[1].clamped)) / _d : 0
            const _k = (vs[1].clamped + _s) / (vs[0].clamped + vs[1].clamped + 2 * _s)
            return { x: _x1 + _k * (_x2 - _x1), y: _y1 + _k * (_y2 - _y1) }
          } else {
            const diff = Math.abs(vs[0].i - vs[1].i)

            const _Rinner = dataObj.R - Q + (diff == 1 ? 1 : 0) * maxScoreRadius
            const _Router = dataObj.R - Q + (diff == 1 ? 1 : 0) * vectorRadius + maxScoreRadius
            const _R = _Rinner + w * (_Router - _Rinner)

            const p = calcCirclePosition(vs[0].x, vs[0].y, _R)
            const _p = calcCirclePosition(vs[1].x, vs[1].y, _R)

            const k = vs[1].clamped / (vs[0].clamped + vs[1].clamped)
            return { x: p.x + k * (_p.x - p.x), y: p.y + k * (_p.y - p.y) }
          }
        } else if (vs.length > 0) {
          const v = vs[0]

          const _q = v.r + vectorGravity + (dataObj.indexes.length > 1 ? 0 : vectorRadius)
          const _r = r + _q + (1 - v.clamped) * (dataObj.indexes.length > 1 ? Q : dataObj.diameter / 2 - _q)

          return {
            x: v.x + _r * Math.cos(degreesToRadians(v.angle)),
            y: v.y + _r * Math.sin(degreesToRadians(v.angle))
          }
        }

        return { x: 0, y: 0 }
      }

      scoresIndexes.forEach((index, i) => {
        const _scores = scores[index].slice(1)

        const _vectors = dataObj.vectors
          .filter(v => _scores[v.i] > 0)
          .map(v => ({ ...v, weight: _scores[v.i], clamped: Math.min(1, Math.max(0, _scores[v.i])) }))

        const _radius = calcRadius(_scores, _vectors)
        const _weight = _vectors.reduce((acc, cur) => acc + cur.clamped, 0) / _vectors.length
        const _position = calcPosition(_scores, _vectors, _radius, _weight)

        const _center = _vectors.length > 1 ? { x: 0, y: 0 } : { x: _vectors[0].x, y: _vectors[0].y }

        const scoreData = {
          i,
          id: scores[index][0],
          x: _position.x,
          y: _position.y,
          r: _radius,
          p: _position,
          d: calcDistance(_center, _position),
          weight: _weight,
          scores: _scores,
          vectors: _vectors
        }
        dataObj.scores.push(scoreData)
      })

      return dataObj
    },
    [
      vectors,
      scores,
      suspicionScores,
      suspicionLevel,
      filter,
      quarantineUIDs,
      vectorRadius,
      vectorGravity,
      minScoreRadius,
      maxScoreRadius,
      maxD,
      minD,
      power
    ]
  )

  // Apply jittering to scores to prevent overlaps
  const jitterScores = useCallback(
    dataObj => {
      const scoresObj = {}
      const R = dataObj.R - vectorRadius + maxScoreRadius

      // Pre-bucket data.scores by vector count / single-vector id once, instead of
      // re-filtering dataObj.scores per vector inside the forEach below.
      const scoresByVectorId = {}
      const multiVectorScores = []
      dataObj.scores.forEach(s => {
        if (s.vectors.length == 1) {
          ;(scoresByVectorId[s.vectors[0].id] ||= []).push(s)
        } else if (s.vectors.length > 1) {
          multiVectorScores.push(s)
        }
      })

      if (jitter) {
        scoresObj.all = { scores: multiVectorScores }
        scoresObj.all.scores.sort((a, b) => a.d - a.weight / 1000 - (b.d - b.weight / 1000))
        dataObj.totals.scores.all = scoresObj.all.scores.map(s => s.id)
        dataObj.totals.links.all = scoresObj.all.scores.flatMap(s => s.vectors.map(v => `${s.id}-${v.id}`))
      } else {
        dataObj.totals.scores.all = multiVectorScores.map(s => s.id)
        dataObj.totals.links.all = multiVectorScores.flatMap(s => s.vectors.map(v => `${s.id}-${v.id}`))
      }

      dataObj.vectors.forEach(v => {
        scoresObj[v.id] = { scores: scoresByVectorId[v.id] || [] }
        scoresObj[v.id].scores.sort((a, b) => b.weight - a.i / 1000 - (a.weight - b.i / 1000))
        dataObj.totals.scores[v.id] = scoresObj[v.id].scores.map(s => s.id)
        dataObj.totals.links[v.id] = scoresObj[v.id].scores.flatMap(s => s.vectors.map(vv => `${s.id}-${vv.id}`))
      })

      const jitterScore = (score, scoresList, i) => {
        const A = Math.floor(360 * Math.random())
        let iteration = 0
        let collided = true

        while (collided && iteration < 100) {
          const collidingScores = scoresList.slice(0, i).filter(s => s.r + score.r > calcDistance(s, score))

          if (!collidingScores.length) {
            collided = false
          } else {
            // D3 v7 quadtree API
            const quadtree = d3
              .quadtree()
              .x(d => d.x)
              .y(d => d.y)
              .addAll(scoresList.slice(0, i))

            quadtree.visit((node, x1, y1, x2, y2) => {
              if (!node.length) {
                const point = node.data
                if (point && point.id != score.id) {
                  const l = calcDistance(point, score)
                  const r = score.r + point.r + padding

                  if (l < r) {
                    if (l > 0) {
                      const x = score.x - point.x
                      const y = score.y - point.y
                      const dd = (2 * (l - r)) / l

                      const p = formatPoint({ x: dd * x, y: dd * y }, degreesToRadians(A))

                      score.x -= p.x
                      score.y -= p.y
                    } else {
                      score.x -= r / 2
                      score.y -= r / 2

                      point.x += r / 2
                      point.y += r / 2

                      const idx = scoresObj.all.scores.findIndex(s => s.id == point.id)
                      if (idx >= 0) {
                        dataObj.scores[point.i] = scoresObj.all.scores[idx] = scoresList[idx] = point
                      }
                    }
                  }
                }
              }

              return x1 > R || x2 < -R || y1 > R || y2 < -R
            })

            dataObj.scores[score.i] = scoresObj.all.scores[i] = scoresList[i] = score
          }

          iteration++
        }
      }

      let _k = 0,
        _offset = 0
      Object.entries(scoresObj).forEach(([key, value]) => {
        // Precompute scores-per-d once per non-'all' bucket; replaces the per-score
        // value.scores.filter(...) which was O(N) per score → O(N²) per bucket.
        let dCounts = null
        if (key != 'all') {
          dCounts = {}
          value.scores.forEach(s => {
            dCounts[s.d] = (dCounts[s.d] || 0) + 1
          })
        }

        value.scores.forEach((score, i) => {
          if (key == 'all') {
            jitterScore(score, value.scores, i)
          } else {
            const _v = score.vectors[0]

            const _sl = dCounts[score.d]
            const _l = _v.bad.start > _v.bad.end ? _v.bad.start - _v.bad.end : 360 - _v.bad.end + _v.bad.start
            const _min = radiansToDegrees(2 * Math.asin((2 * minScoreRadius + vectorGravity / 3) / (2 * score.d)))

            if (0 < i && value.scores[i - 1].d == score.d) {
              _k += 1
            } else {
              _k = 0
              if (1 < dataObj.indexes.length) {
                // `Math.floor(Math.random() * 3) - 1` picks from {-1, 0, 1}
                // (left / no / right shift). The previous expression
                // `3 * Math.floor(Math.random() - 1)` was mathematically always
                // -3 — `Math.random() - 1` is in [-1, 0), Math.floor is always
                // -1 — so the no-shift branch never fired and dots biased
                // counterclockwise.
                const rand = Math.floor(Math.random() * 3) - 1
                if (rand == 0) {
                  _offset = 0
                } else {
                  _offset = rand * (i > 0 ? (Math.random() * (_l - _sl * Math.min(_min, _l / _sl))) / 2 : 0)
                }
              } else {
                // [0, 360) — unbiased; the prior `- 1` could produce -1°.
                _offset = Math.floor(Math.random() * 360)
              }
            }

            const _a = 1 < dataObj.indexes.length ? Math.min(_min, _l / _sl) : Math.max(_min, _l / _sl)
            const angleRad = degreesToRadians(_v.angle - (_a * _sl) / 2 + _k * _a + _offset)

            dataObj.scores[score.i].x =
              scoresObj[key].scores[i].x =
              value.scores[i].x =
                _v.x + score.d * Math.cos(angleRad)
            dataObj.scores[score.i].y =
              scoresObj[key].scores[i].y =
              value.scores[i].y =
                _v.y + score.d * Math.sin(angleRad)
          }
        })
      })

      return dataObj
    },
    [jitter, vectorRadius, maxScoreRadius, minScoreRadius, vectorGravity, padding]
  )

  // Initialize links between scores and vectors
  const initializeLinks = useCallback(
    dataObj => {
      dataObj.scores.forEach(score => {
        const _position = { x: score.x, y: score.y }

        const links = score.vectors.map(v => {
          const points = [{ x: v.x, y: v.y }, _position]
          points.splice(
            1,
            0,
            calcMiddlePoint(points, dataObj.vectors.length < 2 || score.vectors.length > 1 ? curveAngle : 0)
          )

          return {
            id: `vector-${score.id}-${v.id}`,
            start: Array(3).fill({ x: v.x, y: v.y }),
            end: points,
            color: v.color,
            weight: v.weight,
            score,
            vector: v
          }
        })

        dataObj.links = dataObj.links.concat(links)

        dataObj.scores[score.i].arcs = calcArcs(score.id, score.scores, score.vectors, _position, score.r)
        dataObj.scores[score.i].links = links.map(l => l.id)
      })

      return dataObj
    },
    [curveAngle]
  )

  // Calculate starting angle for vectors
  const calcStartAngle = useCallback((dataObj, angle, prevData) => {
    // [0, 360) — unbiased random degree. The prior `- 1` produced [-1, 358],
    // making 359 unreachable and -1 reachable (which worked mod 360 but was off-by-one).
    let start = Math.floor(Math.random() * 360)

    if (prevData && prevData.indexes.length > 1) {
      const added = dataObj.indexes.filter(i => !prevData.indexes.includes(i))
      const removed = prevData.indexes.filter(i => !dataObj.indexes.includes(i))

      if (added.length > 0 || removed.length > 0) {
        const arr = Array.from({ length: 360 }, (_, i) =>
          Math.abs(
            dataObj.indexes.reduce((acc, cur, j) => {
              if (added.includes(cur) || removed.includes(cur)) return acc
              const prevVector = prevData.vectors.find(v => v.i == cur)
              return acc + (prevVector ? prevVector.angle : 0) - (i + j * angle)
            }, 0)
          )
        )
        start = arr.indexOf(Math.min(...arr))
      } else {
        start = prevData.start
      }
    } else if (dataObj.start) {
      start = dataObj.start
    }

    return start
  }, [])

  // Calculate vector positions
  const calcVectorsPositions = useCallback(
    (dataObj, prevData) => {
      if (!dataObj.indexes.length) return dataObj

      const calcBadAngle = (x, y, angle) => {
        if (dataObj.indexes.length > 1) {
          const p = calcCirclePosition(x, y, dataObj.R)
          const P = calcCirclePosition(p.x - x, p.y - y, vectorRadius)

          const _a = radiansToDegrees(Math.atan2(P.y, P.x))
          const normalizedAngle = _a < 0 ? _a + 360 : _a
          const A = Math.min(45, angle)

          return { start: (360 + normalizedAngle - A) % 360, end: (normalizedAngle + A) % 360 }
        }
        return { start: 0, end: 0 }
      }

      dataObj.vectors = []

      if (1 < dataObj.indexes.length) {
        const angle = 360 / dataObj.indexes.length
        const start = calcStartAngle(dataObj, angle, prevData)

        dataObj.indexes.forEach((index, i) => {
          const _angle = degreesToRadians(start + i * angle)
          const _x = (dataObj.diameter * Math.cos(_angle)) / 2
          const _y = (dataObj.diameter * Math.sin(_angle)) / 2

          const vectorData = {
            i: index,
            id: vectors[index].id,
            x: _x,
            y: _y,
            r: vectorRadius,
            color: getVectorColor(vectors[index].id),
            text: vectors[index].text,
            bad: calcBadAngle(_x, _y, angle),
            angle: radiansToDegrees(_angle)
          }
          dataObj.vectors.push(vectorData)
        })

        dataObj.start = start
      } else if (0 < dataObj.indexes.length) {
        const index = dataObj.indexes[0]

        const vectorData = {
          i: index,
          id: vectors[index].id,
          x: 0,
          y: 0,
          r: vectorRadius,
          color: getVectorColor(vectors[index].id),
          text: vectors[index].text,
          bad: { start: 0, end: 0 },
          angle: 0
        }
        dataObj.vectors.push(vectorData)

        dataObj.start = 0
      }

      return dataObj
    },
    [vectors, vectorRadius, calcStartAngle]
  )

  // Calculate score positions
  const calcScoresPositions = useCallback(
    dataObj => {
      if (!dataObj.indexes.length) return dataObj

      dataObj.scores = []
      dataObj.links = []
      dataObj.totals = { scores: {}, links: {} }

      // eslint-disable-next-line no-param-reassign
      dataObj = initializeScores(dataObj)
      // eslint-disable-next-line no-param-reassign
      dataObj = jitterScores(dataObj)
      // eslint-disable-next-line no-param-reassign
      dataObj = initializeLinks(dataObj)

      return dataObj
    },
    [initializeScores, jitterScores, initializeLinks]
  )

  // Main data calculation function
  const calcData = useCallback(
    (reset = false) => {
      // A cached layout places the scores of the payload it was built from, and its key cannot tell
      // two payloads apart: a new payload drops them all, or its first draw is the previous one
      if (reset || dataCacheVersionRef.current !== dataVersion) {
        dataCacheRef.current = {}
        dataCacheVersionRef.current = dataVersion
      }

      if (!vectors || !scores) return

      const enabled = vectors.map((_, i) => i).filter(i => !disabledVectors.includes(vectors[i].id))
      const passing = enabled.filter(i =>
        scores.some(
          s =>
            0 < s[i + 1] &&
            suspicionLevel <= suspicionScores[s[0]] &&
            (filter == 'all' ||
              (!quarantineUIDs.includes(s[0]) && filter != 'quarantined') ||
              (quarantineUIDs.includes(s[0]) && filter != 'flagged'))
        )
      )
      // A filter that no score passes is an empty result, like a payload with no scores: the ring keeps
      // every enabled slot, so it draws the vectors the summary beside it counts
      const indexes = passing.length ? passing : enabled

      // The cached layout depends on more than which vectors are flagged: initializeScores filters
      // the dots by suspicionLevel + filter + the quarantine set and positions them against
      // chartWidth. A live run keeps the cache (reset=false), so any dimension left out of the key
      // serves a stale dot layout mid-run.
      const id = [indexes.join(','), suspicionLevel, filter, chartWidth, quarantineUIDs.join(',')].join('|')

      // `structuredClone` is ~2× faster than JSON round-trip in modern browsers
      // and handles Date/Map/etc. natively. Cache entries must be deep-cloned
      // on both read and write because downstream code mutates nested arrays
      // (scores, links) during layout.
      let newData = dataCacheEnabled && dataCacheRef.current[id] ? structuredClone(dataCacheRef.current[id]) : null

      const prevData = data

      if (null == newData) {
        newData = {
          indexes,
          vectors: [],
          scores: [],
          links: [],
          totals: { scores: {}, links: {} },
          start: dataStartRef.current || 0,
          angle: 0
        }

        newData.center = {
          x: chartWidth / 2,
          y: chartHeight / 2
        }
        newData.diameter = Math.min(chartWidth, chartHeight) - 2 * vectorRadius - 160
        newData.R = newData.diameter / 2 - vectorRadius - maxScoreRadius

        newData = calcVectorsPositions(newData, prevData)
        newData = calcScoresPositions(newData)

        if (dataCacheEnabled) {
          // Bound the cache to the 32 most-recently-used index combinations.
          // The set of reachable combinations is 2^vectorCount (16384 at 14 vectors) —
          // practical usage explores only a handful, but evicting the oldest key on
          // overflow prevents unbounded growth across long-running sessions that
          // never hit resetData.
          const CACHE_CAP = 32
          const keys = Object.keys(dataCacheRef.current)
          if (keys.length >= CACHE_CAP) delete dataCacheRef.current[keys[0]]
          dataCacheRef.current[id] = structuredClone(newData)
        }
      } else if (newData.indexes.length > 1) {
        const angle = 360 / newData.indexes.length
        const start = calcStartAngle(newData, angle, prevData)

        newData.angle = degreesToRadians(start - newData.start)
      }

      // DC-v3: vectors run in parallel. On 'fulfill' (and beyond), pin every
      // visible vector's lifecycle status to 'done' regardless of what the
      // server-side `job.vectors` map happens to contain — matches
      // chart.coffee renderJobDone's simultaneous pin.
      const jobStatus = job?.status
      const fulfillOrDone = jobStatus == 'fulfill' || jobStatus == 'done'
      const visibleRaw = fulfillOrDone ? getVisibleRaw() : null
      const pinnedDone = visibleRaw ? new Set(visibleRaw) : null

      newData.vectors = newData.vectors.map(vector => {
        if (pinnedDone?.has(vector.id) && vector.status !== 'done') {
          return { ...vector, status: 'done' }
        }
        if (job?.vectors?.[vector.id] && vector.status !== job.vectors[vector.id]) {
          return { ...vector, status: job.vectors[vector.id] }
        }
        return vector
      })

      // Store old data for animations
      // newData.old = {
      //   vectors: prevData?.vectors || [],
      //   scores: prevData?.scores || [],
      //   links: prevData?.links || [],
      //   angle: prevData?.angle || 0
      // }

      if (!dataStartRef.current) {
        dataStartRef.current = newData.start
      }

      setData(newData)
    },
    [
      job,
      data,
      vectors,
      scores,
      suspicionScores,
      suspicionLevel,
      filter,
      disabledVectors,
      quarantineUIDs,
      dataVersion,
      chartWidth,
      chartHeight,
      vectorRadius,
      maxScoreRadius,
      dataCacheEnabled,
      calcVectorsPositions,
      calcScoresPositions,
      calcStartAngle
    ]
  )

  // Delayed data calculation
  const delayCalcData = useCallback(
    (reset = false) => {
      if (calcTimerRef.current) clearTimeout(calcTimerRef.current)

      const delay = ['waiting', 'running'].includes(job?.status) ? 100 : 500

      calcTimerRef.current = setTimeout(() => calcData(reset), delay)
    },
    [job, calcData]
  )

  // Recalculate data
  const recalcData = useCallback(() => {
    const reset = !['waiting', 'running'].includes(job?.status)
    delayCalcData(reset)
  }, [job, delayCalcData])

  // Reset data cache
  const resetData = useCallback(() => {
    dataCacheRef.current = {}
    dataStartRef.current = null
  }, [])

  // Effect for job changes.
  // Guarded against the initial mount fire — the next useEffect (on width,
  // vectors, scores, etc.) already fires `recalcData` on first commit, so
  // without this guard `calcData` ran twice back-to-back on every mount.
  const jobMountedRef = useRef(false)
  useEffect(() => {
    if (!jobMountedRef.current) {
      jobMountedRef.current = true
      return
    }
    calcData()
  }, [job]) // eslint-disable-line react-hooks/exhaustive-deps

  // Effect for recalculation triggers
  useEffect(
    () => recalcData(),
    [
      width,
      vectors,
      scores,
      filter,
      suspicionLevel,
      disabledVectors,
      quarantineUIDs,
      vectorRadius,
      vectorGravity,
      minScoreRadius,
      maxScoreRadius,
      curveAngle,
      jitter
    ]
  ) // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (calcTimerRef.current) {
        clearTimeout(calcTimerRef.current)
      }
    }
  }, [])

  return {
    data,
    calcData,
    recalcData,
    resetData
  }
}

export default useDataCalculation
